import { ComponentProps, FC, HTMLAttributes, useEffect, useState } from "react";
import * as _ from "lodash";
import { Space, Typography, DatePicker } from "antd";
import { CalendarOutlined, EditOutlined } from "@ant-design/icons";

import { classname, Epoch, TS } from "@ctra/utils";
import styles from "./Timestamp.module.less";

const { Text } = Typography;

export interface TimestampProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  /**
   * text label to show with the timestamp
   */
  label?: string;
  /**
   * timestamp of epoch to display
   */
  dateTime: number | string;
  /**
   * handle change in timestamp
   */
  onChange?: ComponentProps<typeof DatePicker>["onChange"];
  /**
   * loading indicator
   */
  loading?: boolean;
}

/**
 * Timestamp editor for game plan tasks
 * @param label
 * @param dateTime
 * @param onChange
 * @param loading
 * @param className
 * @param rest
 * @constructor
 */
const Timestamp: FC<TimestampProps> = ({ dateTime, onChange, loading, className, ...rest }) => {
  const [isEditing, setEditing] = useState(false);
  const moment = _.isNumber(dateTime) ? Epoch.asMoment(Epoch.load(dateTime)) : TS.asMoment(dateTime);
  const asDate = moment.format("MMM D, YYYY");

  /**
   * If the date time is changed (aka saved)
   * then we can hide the date picker.
   */
  useEffect(() => {
    setEditing(false);
  }, [dateTime]);

  return (
    <Space align="center" className={classname(className, styles.Sub)} {...rest}>
      <Text className={styles.Text}>
        <CalendarOutlined /> {asDate}
      </Text>
      {onChange ? (
        <>
          <EditOutlined data-testid="edit-timestamp-icon" onClick={() => setEditing(!isEditing)} />
          {isEditing && (
            <DatePicker
              data-testid="edit-timestamp-input"
              className={styles.Sub}
              size="small"
              disabled={loading}
              onChange={onChange}
            />
          )}
        </>
      ) : null}
    </Space>
  );
};

export default Timestamp;
